/**
 * Where tapping a push takes the customer: the store's home page, or another
 * page of the store typed as just its path (the domain is fixed in the
 * field), so merchants never have to write the store's address.
 */
export type StoreLinkMode = 'home' | 'page';

/** "https://loja.com.br/", or '' while the store has no website domain. */
export function storeHomeUrl(domain: string): string {
  const d = domain.trim();
  return d ? `https://${d}/` : '';
}

const SCHEME = /^[a-z][a-z\d+.-]*:\/\//i;
// "loja.com.br", "www.outra.com": a host, not a page like "promo.html".
const LOOKS_LIKE_HOST = /^[\w-]+(\.[\w-]+)*\.[a-z]{2,}$/i;
const FILE_EXTENSION = /\.(html?|php|aspx?|jsp)$/i;

const isStoreHost = (host: string, domain: string) =>
  domain !== '' && (host === domain || host.endsWith(`.${domain}`));

/**
 * What the merchant typed or pasted in "Outra página": the path to show in
 * the field and the full link to send. A whole link of the store (with or
 * without "https://" or "www.") becomes its path; a link of another site or
 * an address that is not a link is kept as typed, so validation names it.
 */
export function parseStorePageInput(
  input: string,
  domain: string
): { path: string; link: string } {
  const raw = input.trim();
  if (raw === '') return { path: '', link: '' };
  const d = domain.trim().toLowerCase();
  const firstSegment = raw.split(/[/?#]/u)[0].toLowerCase();
  const absolute =
    SCHEME.test(raw) ||
    isStoreHost(firstSegment, d) ||
    (LOOKS_LIKE_HOST.test(firstSegment) && !FILE_EXTENSION.test(firstSegment));

  if (absolute) {
    let url: URL;
    try {
      url = new URL(SCHEME.test(raw) ? raw : `https://${raw}`);
    } catch {
      return { path: raw, link: raw };
    }
    if (!isStoreHost(url.hostname.toLowerCase(), d)) {
      return { path: raw, link: url.href };
    }
    const path = `${url.pathname.replace(/^\/+/u, '')}${url.search}${url.hash}`;
    return { path, link: url.href };
  }

  const path = raw.replace(/^\/+/u, '');
  return { path, link: d ? new URL(path, `https://${d}/`).href : '' };
}

/** Mode and path that reproduce `link` (a duplicated campaign's). */
export function storeLinkFields(
  link: string,
  domain: string
): { linkMode: StoreLinkMode; linkPath: string } {
  if (link.trim() === '') return { linkMode: 'home', linkPath: '' };
  const { path } = parseStorePageInput(link, domain);
  const home = path === '' && link.trim() !== '' && isStoreLink(link, domain);
  return home
    ? { linkMode: 'home', linkPath: '' }
    : { linkMode: 'page', linkPath: path };
}

function isStoreLink(link: string, domain: string): boolean {
  try {
    return isStoreHost(
      new URL(link).hostname.toLowerCase(),
      domain.trim().toLowerCase()
    );
  } catch {
    return false;
  }
}
